/**
 * Auth gate — wraps the entire admin app.
 *
 * Two states the gate has to handle:
 *
 *   1. **Not logged in.** Redirect to the customer SPA's login page
 *      with a ``return_to`` param so the user lands back here after
 *      logging in. The customer SPA owns the login UI; we don't
 *      duplicate it on this subdomain.
 *
 *   2. **Logged in but not authorised** (not a SUPER_ADMIN, or org
 *      doesn't have ``is_internal``). Hard-redirect them to
 *      ``app.lenavio.com`` like a customer. Critically, we do NOT
 *      show an error chrome here — the admin dashboard's existence
 *      should not be observable by anyone who isn't already
 *      authorised. A customer SUPER_ADMIN who guesses
 *      "admindash.lenavio.com" should see what looks like a
 *      misdirected login flow, not an "access denied" page that
 *      confirms the surface.
 *
 * Implementation choice: rather than reading ``org.is_internal``
 * from ``/auth/me`` (which we'd have to add to the schema), we use
 * a real admin endpoint (``/admin/overview``) as the gate test. A
 * 200 means the user passed ``require_internal_super_admin``; a 403
 * means they didn't. This way the SPA gate cannot drift from the
 * API gate — they share the same source of truth.
 */
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { AxiosError } from "axios";

import { getOverview } from "@/api/admin";
import { CUSTOMER_APP_URL } from "@/lib/constants";

interface AuthGateProps {
  children: ReactNode;
}

type GateState =
  | "checking"
  | "authorized"
  | "redirecting"
  | "network_error";

// If the gate check is still pending after this many ms, we move
// out of "checking" into "network_error" so the user sees a real
// error UI instead of an infinite spinner. The check normally
// completes in <500ms; 8s is comfortably above the worst
// observed latency.
const CHECK_TIMEOUT_MS = 8_000;

export function AuthGate({ children }: AuthGateProps) {
  const [state, setState] = useState<GateState>("checking");
  // Captured error so the "network_error" UI can show something
  // useful (CORS error class name, HTTP status if any).
  const [errLabel, setErrLabel] = useState<string>("");
  // Re-run trigger for the "Retry" button.
  const [attempt, setAttempt] = useState(0);
  const cancelledRef = useRef(false);

  const runGateCheck = useCallback(async () => {
    setState("checking");
    setErrLabel("");
    const timer = window.setTimeout(() => {
      if (!cancelledRef.current && stateNotResolved()) {
        // Promote the silent stall to a visible error so the
        // founder can act (retry / log in / open the customer
        // SPA). Same UI as a real network failure.
        setErrLabel("Request timed out after 8s.");
        setState("network_error");
      }
    }, CHECK_TIMEOUT_MS);

    // Hack: we can't trivially observe React state from inside
    // the closure. Track the resolved-ness via a ref so the
    // setTimeout callback knows whether to fire.
    let resolved = false;
    const stateNotResolved = () => !resolved;

    try {
      await getOverview();
      resolved = true;
      window.clearTimeout(timer);
      if (!cancelledRef.current) setState("authorized");
    } catch (err) {
      resolved = true;
      window.clearTimeout(timer);
      if (cancelledRef.current) return;

      const axiosErr = err as AxiosError;
      const status = axiosErr?.response?.status;

      if (status === 401) {
        // 401 → the response interceptor in client.ts already
        // navigated to the customer login. Stay in "redirecting"
        // so this component never flashes content.
        setState("redirecting");
        return;
      }
      if (status === 403) {
        // 403 → logged in but not authorized. Hard-redirect to
        // the customer SPA — no error chrome, no breadcrumb that
        // this subdomain even exists.
        try {
          window.location.assign(CUSTOMER_APP_URL);
        } catch {
          /* navigation failed — fall through to error UI */
        }
        setState("redirecting");
        return;
      }

      // No HTTP response at all (CORS-blocked, network down,
      // DNS failure, etc.) OR a 5xx. Surface a real error so
      // the user isn't trapped in an infinite spinner.
      const label =
        status !== undefined
          ? `HTTP ${status}${axiosErr.response?.statusText ? " · " + axiosErr.response.statusText : ""}`
          : axiosErr?.message ||
            "Couldn't reach the API. The browser may have cached a failed CORS preflight.";
      setErrLabel(label);
      setState("network_error");
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    runGateCheck();
    return () => {
      cancelledRef.current = true;
    };
  }, [runGateCheck, attempt]);

  if (state === "authorized") return <>{children}</>;

  if (state === "network_error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white rounded-lg border border-slate-200 p-6">
          <h1 className="text-lg font-semibold text-slate-900">
            Can't reach the API
          </h1>
          <p className="mt-1 text-sm text-slate-600">{errLabel}</p>

          <div className="mt-4 text-xs text-slate-500 space-y-1.5">
            <p className="font-semibold uppercase tracking-wide">
              Try, in order
            </p>
            <ol className="list-decimal list-outside pl-5 space-y-1">
              <li>
                Make sure you're logged in at{" "}
                <a
                  href={CUSTOMER_APP_URL}
                  className="text-accent underline-offset-2 hover:underline"
                >
                  app.lenavio.com
                </a>{" "}
                first — the admin dashboard reuses that session.
              </li>
              <li>
                Hard refresh this page (<kbd>⌘+Shift+R</kbd> /{" "}
                <kbd>Ctrl+Shift+R</kbd>) to drop any cached CORS
                preflight.
              </li>
              <li>
                Open in a private window — fresh CORS cache.
              </li>
              <li>
                If you just deployed: wait ~60 seconds for the
                CORS preflight cache to expire, then retry.
              </li>
            </ol>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setAttempt((n) => n + 1)}
              className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-600"
            >
              Retry
            </button>
            <a
              href={`${CUSTOMER_APP_URL}/login?return_to=${encodeURIComponent(
                window.location.href,
              )}`}
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Log in on app.lenavio.com
            </a>
          </div>
        </div>
      </div>
    );
  }

  // "checking" / "redirecting" both render the neutral splash.
  // We avoid the word "admin", "denied", etc. that would confirm
  // to a probe that this subdomain has a gated surface behind it.
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div
          aria-hidden="true"
          className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-accent"
        />
        <p className="mt-4 text-sm text-slate-500">Loading…</p>
      </div>
    </div>
  );
}
